import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Mail, Send } from 'lucide-react';
import { Button } from './Navigation';
import { useApiClient } from "../../../base/hooks/useApiClient";

export const Contact: React.FC = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [resultMessage, setResultMessage] = useState<string | null>(null);
  const apiClient = useApiClient();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const response = await apiClient.post('send-email', { email, message });

      if (response.ok) {
        setResultMessage(t('contact.form.success'));
        setEmail('');
        setMessage('');
      } else {
        setResultMessage(t('contact.form.error'));
      }
    } catch (error) {
      console.error('Error sending email:', error);
      setResultMessage(t('contact.form.error'));
    }
  };

  return (
      <section id="contact" className="py-16 md:py-24 bg-gradient-to-br from-indigo-50 via-white to-purple-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('contact.title')}
            </h2>
            <p className="text-xl text-gray-600">
              {t('contact.subTitle')}
            </p>
          </div>

          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
              <div className="bg-white rounded-2xl shadow-xl p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">{t('contact.form.title')}</h3>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      {t('contact.form.emailLabel')}
                    </label>
                    <input
                        type="email"
                        id="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        placeholder={t('contact.form.emailPlaceholder')}
                        required
                    />
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                      {t('contact.form.messageLabel')}
                    </label>
                    <textarea
                        id="message"
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        rows={4}
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        placeholder={t('contact.form.messagePlaceholder')}
                        required
                    />
                  </div>
                  <Button primary className="w-full">
                    <Send className="h-5 w-5 mr-2" />
                    {t('contact.form.submit')}
                  </Button>
                </form>
                {resultMessage && (
                    <p className="mt-4 text-center text-sm font-medium text-gray-700">
                      {resultMessage}
                    </p>
                )}
              </div>

              <div className="space-y-8">
                <div className="bg-white rounded-2xl shadow-xl p-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-6">{t('contact.direct.title')}</h3>
                  <div className="space-y-6">
                    <a
                        href="mailto:contact@thesai.ai"
                        className="flex items-center p-4 bg-indigo-50 rounded-xl hover:bg-indigo-100 transition-colors"
                    >
                      <Mail className="h-6 w-6 text-indigo-600" />
                      <div className="ml-4">
                        <div className="font-medium text-gray-900">{t('contact.direct.email')}</div>
                        <div className="text-indigo-600">contact@thesai.ai</div>
                      </div>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
  );
};
